import type {
  AdjustmentOption,
  Alteration,
  AlterationAdjustment,
} from '../api/alterationsApi'

export interface AlterationRow {
  id: string
  label: string
  value: string
  direction: 'increase' | 'decrease' | 'none'
}

/**
 * The adjustments of the most recently finished set, or `[]` when the EPC has
 * no `Finished` set. Other statuses are never shown, and an older finished set
 * is superseded by a newer one rather than merged with it.
 */
export function getLatestFinishedAdjustments(
  alterations: Alteration[],
): AlterationAdjustment[] {
  let latest: Alteration | undefined
  let latestTime = Number.NEGATIVE_INFINITY

  for (const alteration of alterations) {
    if (alteration.status !== 'Finished') continue
    const time = alteration.finishDateTime
      ? Date.parse(alteration.finishDateTime)
      : Number.NaN
    if (Number.isNaN(time)) continue
    if (time > latestTime) {
      latest = alteration
      latestTime = time
    }
  }

  return latest?.alterationAdjustments ?? []
}

const SIGNS: Record<AlterationRow['direction'], string> = {
  increase: '+',
  decrease: '−',
  none: '',
}

function getDirection(value: number): AlterationRow['direction'] {
  if (value > 0) return 'increase'
  if (value < 0) return 'decrease'
  return 'none'
}

/** Always centimetres, in every locale: no unit switch, no imperial. */
export function formatAlterationValue(value: number): string {
  const sign = SIGNS[getDirection(value)]
  return `${sign}${Math.abs(value)}cm`
}

function getOptionLabel(
  option: AdjustmentOption | undefined,
  language: string,
): string | undefined {
  const labels = option?.descriptionLocalized
  // Without an `en` description the row cannot be shown, whatever the locale.
  if (!labels?.en?.trim()) return undefined
  return labels[language]?.trim() || labels.en.trim()
}

/**
 * One row per adjustment (left and right stay separate). An adjustment with no
 * matching option, or no `en` label, is dropped; the other rows still render.
 */
export function buildAlterationRows(
  adjustments: AlterationAdjustment[],
  options: AdjustmentOption[],
  language: string,
): AlterationRow[] {
  const optionsById = new Map(options.map((option) => [option.id, option]))
  const rows: AlterationRow[] = []

  adjustments.forEach((adjustment, index) => {
    const id = adjustment.adjustmentOptionId
    if (!id) return
    const label = getOptionLabel(optionsById.get(id), language)
    if (!label) return

    rows.push({
      id: `${id}-${index}`,
      label,
      value: formatAlterationValue(adjustment.exactAdjustment),
      direction: getDirection(adjustment.exactAdjustment),
    })
  })

  return rows
}
